import { useEffect, useMemo, useState } from "react";

import { useThemeOptions } from "./hooks/useThemeOptions";
import { useSourcingSettings } from "./hooks/useSourcingSettings";
import { cn } from "@utils/tailwind.util";
import { arraysEqual } from "@utils/array.util";
import { findDeepestSelectedThemeInBranch, hasSelectedSiblingUnderParent } from "./utils";
import { ThemeRelationMode } from "@enums/relations.enum";
import { IBaseTheme } from "@interfaces/sourcing.interface";
import { ThemeTreeSelect } from "./ThemeTreeSelect";
import { ThemeComponent } from "./ThemeComponent";

interface Props {
  selectedThemes: IBaseTheme[];
  onRemoveTheme: (currentTheme: IBaseTheme, parentTheme: IBaseTheme | null) => void;
  onAddTheme: (theme: IBaseTheme, onSuccess?: () => void) => void;
  triggerClassName?: string;
}

export const ThemeComponentWithThemes = ({
  selectedThemes,
  onRemoveTheme,
  onAddTheme,
  triggerClassName,
}: Props) => {
  const { themeOptions, loadingThemeIds } = useThemeOptions();
  const { data: sourcingSettings } = useSourcingSettings();

  const [values, setValues] = useState<string[]>([]);

  const isMultipleSelect = sourcingSettings?.relation_mode === ThemeRelationMode.MULTI;
  const hasSingleZeroLevelTheme = sourcingSettings?.zero_level_count === 1;

  const serializedThemeOptions = JSON.stringify(themeOptions);
  const stableOptions = useMemo(() => {
    // if we have a single level zero theme, than just skip it and return it's children
    return hasSingleZeroLevelTheme ? themeOptions[0]?.children || [] : themeOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedThemeOptions, hasSingleZeroLevelTheme]);

  const handleSelect = (theme: IBaseTheme) => {
    if (values.includes(theme.id)) {
      const deepestSelected = findDeepestSelectedThemeInBranch(theme, selectedThemes);
      const themeToRemove = deepestSelected ?? theme;

      let parentTheme: IBaseTheme | null =
        theme?.parent_list?.[theme?.parent_list?.length - 2] ?? null;

      if (parentTheme) {
        // items in parent_list field don't have the parent_list
        const parent_lsit = theme?.parent_list.slice(0, theme?.parent_list?.length - 1) || [];
        parentTheme = { ...parentTheme, parent_list: parent_lsit };
      }

      // Don't re-tag the parent if another selected theme still lives under it: the BE collapses a
      // parent tag onto its whole subtree, so re-tagging would drop that still-selected sibling.
      const hasSiblingUnderParent =
        !!parentTheme &&
        hasSelectedSiblingUnderParent(parentTheme.id, themeToRemove.id, selectedThemes);

      // if we have a single level zero theme, we should not tag zero level theme
      const shouldTagParentTheme =
        !hasSiblingUnderParent &&
        (!hasSingleZeroLevelTheme || themeOptions[0]?.id !== parentTheme?.id);

      onRemoveTheme(themeToRemove, shouldTagParentTheme ? parentTheme : null);
    } else {
      onAddTheme(theme);
    }
  };

  useEffect(() => {
    const uniqueIds = Array.from(
      new Set(
        selectedThemes.flatMap((theme) => theme.parent_list?.map((parent) => parent.id) || [])
      )
    );

    // ids of all selected themes with their parents
    // only update state if values actually changed
    if (!arraysEqual(values, uniqueIds)) {
      setValues(uniqueIds);
    }
  }, [selectedThemes, values]);

  return (
    <div className="flex flex-col gap-2">
      <div
        id="company-themes"
        className={cn("relative flex flex-col gap-1", {
          "flex-row flex-wrap items-center": !!values.length,
        })}
      >
        {selectedThemes.map((theme, i) => (
          <div key={theme?.id ?? i} className="flex items-center gap-0.5">
            <ThemeComponent
              currentTheme={theme ?? undefined}
              isLevelOneBased={hasSingleZeroLevelTheme}
              onRemove={onRemoveTheme}
            />
          </div>
        ))}

        <ThemeTreeSelect
          onSelect={handleSelect}
          options={stableOptions}
          initialValues={values}
          selectedBranches={selectedThemes}
          isMultipleSelect={isMultipleSelect}
          loadingThemeIds={loadingThemeIds}
          isLevelOneBased={hasSingleZeroLevelTheme}
          triggerClassName={triggerClassName}
        />
      </div>
    </div>
  );
};
